import { matchPath } from 'react-router-dom';
import type { Agent } from '@/types/Agents';
import { AGENT_TABS, resolveAgentTab } from '@/utils/agentTabs';
import { AUTH_ROUTES } from '@/utils/auth';

/** Product name, always the last segment of the document title. */
export const APP_NAME = 'Dialup';

/** Matches the separator the static title in index.html already used. */
const TITLE_SEPARATOR = ' - ';

/**
 * Past this the counter stops being information and starts being noise — and
 * a narrow tab only shows the first few characters anyway.
 */
const MAX_UNREAD_IN_TITLE = 99;

/**
 * Routes whose title is a single fixed label. Everything that depends on
 * loaded data — agents, conversations — is resolved separately below.
 */
const STATIC_TITLES: Record<string, { key: string; fallback: string }> = {
  '/': { key: 'common.home', fallback: 'Home' },
  '/home': { key: 'common.home', fallback: 'Home' },
  '/billing': { key: 'sidebar.billing.translation', fallback: 'Billing' },
  '/account': { key: 'sidebar.account.translation', fallback: 'Account' },
  '/help': { key: 'sidebar.help.translation', fallback: 'Help' },
  '/change-password': {
    key: 'change_password.form.submit.translation',
    fallback: 'Change Password',
  },
  '/agents/new': {
    key: 'agent.list.new_agent.title.translation',
    fallback: 'New Agent',
  },
  '/design-system': {
    key: 'page_title.design_system.translation',
    fallback: 'Design system',
  },
  [AUTH_ROUTES.login]: { key: 'login.title.translation', fallback: 'Login' },
  [AUTH_ROUTES.verifyEmail]: {
    key: 'page_title.verify_email.translation',
    fallback: 'Verify your email',
  },
  [AUTH_ROUTES.forgotPassword]: {
    key: 'forgot_password.title.translation',
    fallback: 'Reset your password',
  },
  [AUTH_ROUTES.resetPassword]: {
    key: 'reset_password.title.translation',
    fallback: 'Set a new password',
  },
};

const CONVERSATION_PATH = '/agents/:agent_id/conversation/:conversation_id';
const AGENT_PATH = '/agents/:agent_id/*';

interface PageTitleContext {
  pathname: string;
  /** The location's query string, which carries the agent page's active tab. */
  search: string;
  agents: Agent[];
  /** i18next's `t`, narrowed to the key-plus-fallback call this module makes. */
  translate: (key: string, fallback: string) => string;
}

/**
 * Names the current page, most specific part first: `['Conversations',
 * 'Sales agent']` for an agent's conversations tab. Empty while the page
 * cannot be named yet.
 */
export function resolvePageTitleSegments({
  pathname,
  search,
  agents,
  translate,
}: PageTitleContext): string[] {
  const staticTitle = STATIC_TITLES[pathname];
  if (staticTitle) return [translate(staticTitle.key, staticTitle.fallback)];

  const conversationMatch = matchPath(CONVERSATION_PATH, pathname);
  const agentMatch = conversationMatch ?? matchPath(AGENT_PATH, pathname);

  if (agentMatch) {
    const agent = agents.find(
      candidate => candidate.id?.toString() === agentMatch.params.agent_id
    );

    // The agents slice is still loading on a cold open of a deep link. Naming
    // the tab without the agent it belongs to would be misleading, so stay on
    // the bare product name until the name arrives.
    if (!agent) return [];

    if (conversationMatch) {
      return [
        translate(
          'header.breadcrumbs.agent.conversation.translation',
          'Conversation'
        ),
        agent.name,
      ];
    }

    const activeTab = resolveAgentTab(
      agent,
      new URLSearchParams(search).get('tab')
    );
    const tab = AGENT_TABS.find(candidate => candidate.value === activeTab);

    return [tab ? translate(tab.labelKey, tab.fallbackLabel) : '', agent.name];
  }

  return [translate('not_found.heading.translation', 'Page not found')];
}

/**
 * Joins the segments into the document title, with any unread notifications
 * announced up front where a narrow tab still shows them, attached to the page
 * name rather than separated from it: `(2) Conversations - Sales agent - Dialup`.
 */
export function buildDocumentTitle(
  segments: Array<string | null | undefined>,
  unreadCount = 0
): string {
  const parts = segments
    .map(segment => segment?.trim())
    .filter((segment): segment is string => Boolean(segment));

  const title = [...parts, APP_NAME].join(TITLE_SEPARATOR);

  if (unreadCount <= 0) return title;

  const badge =
    unreadCount > MAX_UNREAD_IN_TITLE
      ? `${MAX_UNREAD_IN_TITLE}+`
      : String(unreadCount);

  return `(${badge}) ${title}`;
}
